import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, SPRING, SOFT, FADE, STAGE, useToasts, useClock, useHash, useEsc, useOverlayLock, stop, go, parseHashRoute } from "./ui.js";
import { OpsMap } from "./ops.js";
import Settings from "./settings.js";
import SA from "./sa.js";
import System from "./system.js";
import { Login, Signup, Forgot, SignOut } from "./auth.js";
import Activate, { activatePreviewV3 } from "./activate.js";
import Response from "./response.js";
import PreparePrimary from "./prepare-primary.js";
import DispatchPrimary from "./dispatch-primary.js";
import PlacesPrimary from "./places-primary.js";
import SkillsPrimary from "./skills-primary.js";
import CamerasPrimary from "./cameras-primary.js";
import Fleet from "./fleet.js";
import EdgeOS from "./edgeos.js";
import MachineDeck from "./machine-deck.js";
import MachineDetail from "./machine-detail.js";
import MachineGroups from "./machine-groups.js";
import MachineCapDeploy from "./machine-cap-deploy.js";
import PlatformControl from "./platform-control-desk.js";
import AutomationsPrimary from "./automations-primary.js";
import OtaPrimary from "./ota-primary.js";
import { Overlay, COLD } from "./loading.js";
import { SessionProvider, useSession } from "./session.js";
import { LiveProvider, useLive } from "./live.js";
import { resolveCanon, ROUTE_ALIASES } from "./route-mask.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { RoutePaintVeil, primaryOf } from "./route-stage-fence.js";
import { PRIMARY_NAV, CAP_HOME, paletteScreenItems } from "./cmc-entry-registry.js";
import { hasExplicitSaSeat, canOpenHiddenLab, canOpenActivateSeat } from "./cap-complete.js";
import { isLocalHost } from "./pathseal.js";
import { path, useApi } from "./api.js";
import {
  LocationBar,
  notifDeepLinkOf,
  buildHandoffHref,
} from "./shell-wayfind.js";
import { QualityNetBanner, assertNoServiceWorkerRegistered } from "./quality-runtime.js";
export { resolveCanon, ROUTE_ALIASES };
/* Soft≠Live bookmark aliases (incl. "/logout": "/auth/signout") live in ROUTE_ALIASES. */
/* Soft≠Live IA3-1 / AIW-5 / AUTO-1 / OTA-1: committed primaries.
   Fleet/Edge/Control lab-hidden. Automations eighth. OTA is SA-only.
   FOUND-1: PRIMARY_NAV is the single label/path SoT for Topbar. */
const NAV = PRIMARY_NAV;

const WORKSPACE_SAY = Object.freeze({
  places: "Draw the site",
  prepare: "Rehearse a drill",
  skills: "Board warehouse",
  activate: "Claim a board",
  automations: "Publish a rule",
  ota: "Board images",
  settings: "Seat and site",
  sa: "Platform seats",
});

/* Platform / Control / OTA are staff ground. Showing a tab the
   grant cannot open teaches the console lies about what they can reach. */
const navFor = me => NAV.filter(n => {
  if (n.lab) return false; /* IA3-1: hide Fleet/Edge/Control from topbar */
  if (n.id === "activate") return canOpenActivateSeat(me);
  if (n.id === "prepare" || n.id === "automations") {
    /* Users operate the floor. Rehearsal and rule publishing are admin house work. */
    if (!me) return false;
    const r = String(me.role || "");
    if (r === "superadmin" || r === "admin") return true;
    if (r === "sa_staff") return hasExplicitSaSeat(me);
    return false;
  }
  if (n.id === "skills") {
    if (!me) return false;
    const r = String(me.role || "");
    if (r === "superadmin" || r === "admin") return true;
    const caps = Array.isArray(me.caps) ? me.caps : [];
    return caps.includes("sa_full_access") || caps.includes("sa_ota_admin");
  }
  if (n.ota) {
    if (!me) return false;
    const r = String(me.role || "");
    if (r === "superadmin") return true;
    const caps = Array.isArray(me.caps) ? me.caps : [];
    return caps.includes("sa_full_access") || caps.includes("sa_ota_admin");
  }
  if(n.sa){
    if(!me) return false;
    const r = String(me.role || "");
    /* CAP-COMPLETE: exact SA seat only — never caps.length heuristic. */
    if(r === "superadmin") return true;
    return hasExplicitSaSeat(me);
  }
  return true;
});

/** Deep-link door for Control / Fleet / Edge lab — SA exact seat only. */
function canOpenControl(me) {
  return canOpenHiddenLab(me);
}

/* ═══════════════════ top bar ═══════════════════════════════════════════ */

/* The bar used to fit by hand-tuned breakpoints, and they went stale the moment
   a nav label changed. At 1301px it wanted 1603px and silently clipped the
   notification bell and the avatar off the right edge — the two controls an
   operator reaches for first. So it measures itself instead. Each step drops the
   next-least-important cluster and re-measures, which means the ladder is derived
   from what is actually rendered rather than from a guess about it. */
const FIT_MAX = 6;
function useFit(ref, key){
  useEffect(()=>{
    const el = ref.current; if(!el) return;
    let raf = 0;
    /* scrollWidth is not the truth on this element: the alarm dot and the nav
       pill are absolutely positioned and inflate it even when the row fits. The
       question the operator actually asks is whether the last control still
       lands inside the bar, so measure that instead. */
    const overflows = ()=>{
      /* Probe the exit control itself — topbar__end's border-box can still “fit”
         while Sign out overflows past the bar (flex children paint outside). */
      const end = el.querySelector("[data-cmc-chrome-end]") || el.lastElementChild;
      const probe = el.querySelector("[data-cmc-signout=\"chrome\"]")
                 || (end && end.lastElementChild)
                 || end;
      if(!probe) return false;
      const pad = parseFloat(getComputedStyle(el).paddingRight) || 0;
      const barBox = el.getBoundingClientRect();
      const endOut = probe.getBoundingClientRect().right > barBox.right - pad + 1;
      /* Single-row chrome only — a wrapped second band is a Soft density failure.
         Climb the fit ladder instead of letting the bar grow to ~2× height. */
      const stacked = barBox.height > 70;
      /* With nowrap + horizontal nav scroll, clip is OK; still climb when Sign out
         or the end cluster leaves the bar. Do not require nav.clientWidth>=168 —
         that forced wrap on medium widths for long tenant straps. */
      const endClipped = !!(end && end.scrollWidth > end.clientWidth + 2);
      return endOut || stacked || endClipped;
    };
    const measure = ()=>{
      raf = 0;
      let step = 0;
      el.dataset.fit = "0";
      /* Each write forces the reflow that tells us whether the step was enough.
         Capped, so a pathological width cannot spin. */
      while(step < FIT_MAX && overflows()){ step += 1; el.dataset.fit = String(step); }
    };
    const schedule = ()=>{ if(!raf) raf = requestAnimationFrame(measure); };
    schedule();
    /* Watching the bar catches viewport changes; watching the children catches
       content ones — a webfont swapping in after first paint widens every label
       at once, and a one-shot measurement would never notice. */
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    [...el.children].forEach(c=>ro.observe(c));
    if(document.fonts?.ready) document.fonts.ready.then(schedule);
    return ()=>{ ro.disconnect(); if(raf) cancelAnimationFrame(raf); };
  },[key]);
}

function TopBar({ route, clock, open, setOpen, onPalette, me }){
  const bar = React.useRef(null);
  const live = useLive();
  /* SHELL-1: Needs You = exception queue (home-context waiting_n), not alarm count. */
  const homeCtx = useApi(path("get__ai_home-context"));
  const maic = (homeCtx.data && homeCtx.data.maic) || {};
  const needsYouN = Number(maic.waiting_n || 0);
  /* Alarms, events, belief and the device register come from the live feed.
     An empty register means this account has no devices, not that fourteen
     demo ones should be painted in. */
  const s = live ? {
    alarms: live.alarms || [],
    events: live.events || [],
    belief: live.belief,
    offline: live.offline || [],
    devices: live.devices || [],
  } : { alarms: [], events: [], belief: null, devices: [], offline: [] };
  useFit(bar, route);
  const total = s.devices.length;
  const online = total - s.offline.length;
  const liveErr = String((live && live.error) || "");
  const onlineThrottled = /\b429\b|too many/.test(liveErr.toLowerCase());
  const alarmN = s.alarms.length;
  /* Match by section root (/response/*), not only the default leaf path
     (/response/board/) — otherwise Roster never lights the pill. */
  const visibleNav = navFor(me);
  const active = NAV.find(n=>{
    const root = "/" + n.id;
    return route === root || route.startsWith(root + "/")
        || route === n.path || route.startsWith(n.path + "/");
  }) || (route.startsWith("/settings") ? NAV.find(n=>n.id==="settings")
      :  route.startsWith("/ops") ? NAV[0] : null);
  const pinnedIds = new Set(["ops", "cameras", "response", "dispatch", "machine"]);
  const pinnedNav = visibleNav.filter((n) => pinnedIds.has(n.id));
  const workspaceNav = visibleNav.filter((n) => !pinnedIds.has(n.id));
  const activeWorkspace = workspaceNav.find((n) => n.id === active?.id) || null;
  useEffect(()=>{
    const nav = bar.current && bar.current.querySelector(".nav");
    const on = nav && nav.querySelector('button[data-on="1"]');
    if (on && typeof on.scrollIntoView === "function") {
      on.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
    }
  }, [active && active.id]);
  const openNeedsYou = () => {
    const href = buildHandoffHref(CAP_HOME.needs_you.path, { return_to: route });
    go(href);
  };
  const openNotif = (ev) => {
    setOpen(null);
    go(notifDeepLinkOf(ev, route));
  };
  return html`
    <${M.header} ref=${bar} className="topbar glass" data-cmc-shell="shell-1"
        initial=${{ y:-36, opacity:0 }} animate=${{ y:0, opacity:1 }}
        transition=${{ ...SOFT, stiffness:360 }}>
      <button type="button" className="mark" onClick=${()=>go("/ops")}
          style=${{ textDecoration:"none", color:"inherit", background:"none", border:0, cursor:"pointer", font:"inherit" }}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 2.6 20.4 6v6.2c0 5-3.5 8.8-8.4 10.4C7.1 21 3.6 17.2 3.6 12.2V6z"
            stroke="var(--aqua)" strokeWidth="1.4" strokeLinejoin="round"/>
          <path d="M12 8v8M8.4 11.2 12 8l3.6 3.2" stroke="var(--aqua)" strokeWidth="1.4"
            strokeLinecap="round" strokeLinejoin="round" opacity=".62"/>
        </svg><b>SENTINEL</b>
      </button>

      <nav className="nav" aria-label="Primary">
        ${pinnedNav.map(n=> html`
          <${M.button} key=${n.id} type="button" onClick=${()=>go(n.path)}
              data-on=${active?.id===n.id?"1":"0"} data-ai=${n.ai?"1":"0"} data-nav-id=${n.id}
              aria-current=${active?.id===n.id ? "page" : undefined}
              whileHover=${{ y: -1 }} whileTap=${{ scale: 0.98 }} transition=${SPRING}>
            ${active?.id===n.id && html`<${M.span} layoutId="nav-pill" className="nav-pill" transition=${SPRING}
              style=${n.ai?{ background:"var(--ai-dim)", borderColor:"rgba(150,166,255,.3)" }:null}/>`}
            <span className="nav__label">${n.label}</span>
            ${n.ai && html`<span className="badge" data-tone="ai">O9</span>`}
          <//>`)}
      </nav>

      <div className="topbar__workspace-menu">
        <${M.button} type="button" className="topbar__workspace-trigger"
          data-on=${activeWorkspace ? "1" : "0"}
          aria-haspopup="menu" aria-expanded=${open === "workspaces" ? "true" : "false"}
          onClick=${(e)=>{ stop(e); setOpen(open === "workspaces" ? null : "workspaces"); }}
          whileTap=${{ scale:.98 }} transition=${SPRING}>
          <${Ico} d="grid" w=${14}/>
          <span>${activeWorkspace ? activeWorkspace.label : "House"}</span>
          <${Ico} d="arrowR" w=${11}/>
        <//>
        <${AnimatePresence}>
          ${open === "workspaces" ? html`<${M.div} className="pop topbar__workspace-pop" role="menu"
            initial=${{opacity:0,y:-5,scale:.98}} animate=${{opacity:1,y:0,scale:1}}
            exit=${{opacity:0,y:-4,scale:.985}} transition=${SOFT} onClick=${stop}>
            <div className="topbar__workspace-pop-head">
              <span>House</span><small>Not the live floor</small>
            </div>
            <div className="topbar__workspace-grid">
              ${workspaceNav.map((n)=>html`<button key=${n.id} type="button" role="menuitem"
                data-on=${active?.id===n.id?"1":"0"}
                onClick=${()=>{ setOpen(null); go(n.path); }}>
                <span><b>${n.label}</b><small>${WORKSPACE_SAY[n.id] || "Open"}</small></span>
                <${Ico} d="arrowR" w=${12}/>
              </button>`)}
            </div>
          <//>` : null}
        <//>
      </div>

      <div className="topbar__end" data-cmc-chrome-end="1" data-cmc-ia="ia3-3" data-cmc-shell="shell-1">
        <button type="button" className="site" data-pri="3" data-cmc-ia-site="1"
          data-cmc-shell-site="org"
          title="Site scope — organisation Sites (not Areas / Groups / Zones)"
          onClick=${() => go(CAP_HOME.sites.path)}>
          <b>${me.tenant || me.username}</b>
          <span>Site scope · Areas on map</span>
        </button>

        <button type="button" className="vital" data-pri="2"
          data-tone=${needsYouN > 0 ? "warn" : "ai"}
          data-cmc-ia-needs="1"
          data-cmc-shell-needs="exception"
          title=${CAP_HOME.needs_you.label + " — exceptions only (not alarms)"}
          onClick=${openNeedsYou}>
          <u>Needs you</u><b> ${homeCtx.loading && homeCtx.data == null ? "…" : String(needsYouN)}</b>
        </button>

        <button type="button" className="search" onClick=${onPalette} aria-label="Search — command palette">
          <${Ico} d="search" w=${14}/>
          <input placeholder="Search" readOnly tabIndex=${-1}/>
          <kbd>⌘K</kbd>
        </button>

        <button type="button" className="topbar__launcher" data-cmc-ia-launcher="1" data-pri="4"
          title="Launcher · More desks"
          aria-label="Launcher More" onClick=${onPalette}>
          <${Ico} d="grid" w=${14}/><span>More</span>
        </button>

        <div className="vital" data-pri="5" data-tone=${!total?(onlineThrottled?"warn":""):s.offline.length?"warn":"live"}
          data-cmc-ia-offline=${s.offline.length ? "1" : "0"}
          data-cmc-online-live="1"
          data-cmc-online-throttled=${onlineThrottled ? "1" : "0"}
          title=${onlineThrottled
            ? (total
              ? (`Live GET /devices throttled (429) · holding last answer Soft≠ invent · ${online}/${total}`)
              : "Live GET /devices throttled (429) · register unread Soft≠ invent")
            : (total
              ? (`Live GET /devices · ${online} answering / ${total} listed`
                + (s.offline.length ? (` · ${s.offline.length} quiet`) : "")
                + " · poll ~45s Soft≠ invent")
              : "Live device register unread")}>
          <u>online</u><b>${total ? `${online}/${total}` : (onlineThrottled ? "…" : "—")}</b></div>
        <button type="button" className="vital" data-tone=${alarmN ? "warn" : ""} data-pri="3"
          data-cmc-shell-alarms="1"
          title="Open alarms (named separately from Needs You)"
          onClick=${() => go(buildHandoffHref("/ops/alarms", { return_to: route }))}>
          <u>alarms</u><b>${String(alarmN)}</b></button>
        <div className="vital" data-tone="ai" data-pri="2"><u>belief</u><b>${s.belief?s.belief.conf:"—"}</b></div>
        <div className="vital" data-pri="1"><u>your time</u><b>${clock}</b></div>
        <div className="topbar__slot">
          <button type="button" className="icon-btn"
              aria-label=${alarmN ? ("Notifications · " + alarmN + " alarms") : "Notifications"}
              onClick=${e=>{ stop(e); setOpen(o=>o==="bell"?null:"bell"); }}>
            <${Ico} d="bell" w=${15}/>${alarmN>0 && html`<span className="dot"/>`}
          </button>
          <${AnimatePresence}>
            ${open==="bell" && html`
              <${M.div} key="b" className="pop glass" initial=${{ opacity:0, y:-10, scale:.96 }}
                  animate=${{ opacity:1, y:0, scale:1 }} exit=${{ opacity:0, y:-8, scale:.97 }}
                  transition=${FADE} onClick=${stop} data-cmc-shell="notif-pop">
                <div className="pop__in">
                  <h3>Recent</h3>
                  ${s.events.slice(0,5).map((e,i)=> html`
                    <button key=${i} type="button" className="notif"
                        data-tone=${e.lvl==="warn"?"warn":e.lvl==="info"?"":"bad"}
                        data-cmc-shell-notif="1"
                        onClick=${() => openNotif(e)}>
                      <i/><span className="txt"><b>${e.txt}</b><span className="sub">${e.ts}</span></span>
                    </button>`)}
                  ${!s.events.length && html`<p className="note">Nothing recent.</p>`}
                </div>
              <//>`}
          <//>
        </div>

        <div className="topbar__slot">
          <button type="button" className="avatar" aria-label=${"Account menu · " + (me.username || "operator")}
              onClick=${e=>{ stop(e); setOpen(o=>o==="user"?null:"user"); }}>${me.initials}</button>
          <${AnimatePresence}>
            ${open==="user" && html`
              <${M.div} key="u" className="pop glass" initial=${{ opacity:0, y:-10, scale:.96 }}
                  animate=${{ opacity:1, y:0, scale:1 }} exit=${{ opacity:0, y:-8, scale:.97 }}
                  transition=${FADE} onClick=${stop}>
                <div className="pop__in">
                  <div className="u-row">
                    <span className="avatar">${me.initials}</span>
                    <span className="txt"><b>${me.username}</b><span className="sub">${me.email || me.role}</span></span>
                  </div>
                  <button type="button" className="pop-item" onClick=${()=>go("/settings/profile")}><${Ico} d="user" w=${14}/>Profile</button>
                  <button type="button" className="pop-item" onClick=${()=>go("/settings/security")}><${Ico} d="lock" w=${14}/>Password</button>
                  <button type="button" className="pop-item" onClick=${()=>go("/settings/sessions")}><${Ico} d="clock" w=${14}/>Sessions</button>
                  ${hasExplicitSaSeat(me) ? html`
                    <button type="button" className="pop-item" data-cmc-sa-chrome="1" onClick=${()=>go("/sa")}><${Ico} d="shield" w=${14}/>Platform</button>` : null}
                  <div className="pop-sep"/>
                  <button type="button" className="pop-item pop-item--out" data-cmc-signout="menu"
                      onClick=${()=>go("/auth/signout")}>
                    <${Ico} d="logout" w=${14}/>Sign out<span className="tail">${me.username}</span></button>
                </div>
              <//>`}
          <//>
        </div>

        <button type="button" className="topbar__out" data-cmc-signout="chrome"
            aria-label="Sign out" title="Sign out"
            onClick=${()=>go("/auth/signout")}>
          <${Ico} d="logout" w=${14}/><span>Sign out</span>
        </button>
      </div>
    <//>`;
}

/* ═══════════════════ command palette ═══════════════════════════════════ */
function Palette({ open, onClose, toast }){
  const { me } = useSession();
  const live = useLive();
  const [q,setQ] = useState("");
  const [i,setI] = useState(0);
  useOverlayLock(open);
  const devices = (live && live.devices) || [];
  const offline = (live && live.offline) || [];
  const items = useMemo(()=>{
    const nav = navFor(me).map(n=>({ k:n.label, hint:"go", run:()=>go(n.path) }));
    const screens = paletteScreenItems(go, me);
    const devs = devices.map((d) => ({ k:d.label, hint:d.id, state:offline.includes(d.id)?"off":"on",
      run:()=>go("/machine/"+encodeURIComponent(d.id)) }));
    const saOk = !!(me && (String(me.role||"") === "superadmin" || String(me.role||"") === "sa_staff" || hasExplicitSaSeat(me)));
    const acts = [
      { k:"Silence sirens…", hint:"action", run:async ()=>{
          /* SAFE-1: launcher never one-clicks hardware. Preview + confirm, then Alerts desk owns blast. */
          const ids = devices.filter((d) => d.kind === "siren" || (live && live.alarms || []).some((a) => a.dev === d.id)).map((d) => d.id);
          const uniq = [...new Set(ids.length ? ids : [])].slice(0, 40);
          if (!uniq.length) {
            toast("No active sirens to preview — open Alerts", "warn");
            go("/ops/alarms");
            return;
          }
          const targets = uniq.map((id) => {
            const d = devices.find((x) => x.id === id);
            return { id, label: (d && d.label) || id };
          });
          const ok = await confirmDeviceCommand({
            kind: "bulk_silence",
            targets,
            detail: "Launcher only opens Alerts after you confirm. Silence itself still runs from the Alerts desk with Live seat checks.",
            confirmLabel: "Open Alerts desk",
          });
          if (!ok) return;
          go("/ops/alarms");
        } },
      { k:"Open Mission Room", hint:"action", run:()=>go("/response/cases") },
      { k:"Machine health", hint:"action", run:()=>go("/machine?view=health") },
      { k:"Skills rollout", hint:"action", run:()=>go("/skills/waves") },
      ...(saOk ? [{ k:"Platform Edge Lab", hint:"lab", run:()=>go("/sa/edge-lab") }] : []),
    ];
    const all = [...nav,...screens,...devs,...acts];
    return q ? all.filter(x=>(x.k+x.hint).toLowerCase().includes(q.toLowerCase())) : all.slice(0,10);
  },[q,me,devices,offline,live,toast]);

  useEffect(()=>{ setI(0); },[q]);
  useEffect(()=>{ if(open){ setQ(""); setI(0); } },[open]);
  useEffect(()=>{
    if(!open) return;
    const on = e => {
      if(e.key==="ArrowDown"){ e.preventDefault(); setI(v=>Math.min(v+1, items.length-1)); }
      if(e.key==="ArrowUp"){ e.preventDefault(); setI(v=>Math.max(v-1,0)); }
      if(e.key==="Enter" && items[i]){ e.preventDefault(); items[i].run(); onClose(); }
    };
    addEventListener("keydown",on); return ()=>removeEventListener("keydown",on);
  },[open,items,i,onClose]);

  if(!open) return null;
  return html`
    <${M.div} className="scrim" style=${{ alignItems:"flex-start" }} initial=${{ opacity:0 }}
        animate=${{ opacity:1 }} exit=${{ opacity:0 }} transition=${FADE} onClick=${onClose}>
      <${M.div} className="palette glass" initial=${{ scale:.97, y:-10 }} animate=${{ scale:1, y:0 }}
          exit=${{ scale:.98, y:-6 }} transition=${SOFT} onClick=${stop}>
        <div className="palette__q">
          <${Ico} d="search" w=${17}/>
          <input autoFocus value=${q} onChange=${e=>setQ(e.target.value)} placeholder="Jump to a screen, a device, or an action"/>
          <kbd style=${{ fontFamily:"var(--mono)", fontSize:"var(--t-micro)", color:"var(--fg-4)",
            border:"1px solid rgba(255,255,255,.1)", borderRadius:5, padding:"2px 7px" }}>esc</kbd>
        </div>
        <div className="palette__list">
          ${items.length ? items.map((x,n)=> html`
            <button key=${x.k+n} className="pal-item" data-hl=${n===i?"1":"0"} data-state=${x.state||""}
                onMouseEnter=${()=>setI(n)} onClick=${()=>{ x.run(); onClose(); }}>
              ${x.state && html`<span className="st"/>`}
              <b>${x.k}</b><span className="hint2">${x.hint}</span>
            </button>`) : html`<div className="pal-empty">Nothing matches “${q}”.</div>`}
        </div>
      <//>
    <//>`;
}

/* ═══════════════════ toasts ════════════════════════════════════════════ */
const Toasts = ({ list }) => html`
  <div className="toasts">
    <${AnimatePresence}>
      ${list.map(t=> html`
        <${M.div} key=${t.key} className="toast glass" data-tone=${t.tone||""}
            initial=${{ opacity:0, y:-12, scale:.96 }} animate=${{ opacity:1, y:0, scale:1 }}
            exit=${{ opacity:0, y:-8, scale:.97 }} transition=${SOFT}>
          <${Ico} d=${t.tone==="bad"?"alert":t.tone==="warn"?"info":"check"} w=${14}/>
          <span className="z">${t.msg}</span>
        <//>`)}
    <//>
  </div>`;

/* ═══════════════════ routing table ═════════════════════════════════════
   Every reachable address is declared here. A sub-route that is not on its
   list is a 404, not a silent fall-back to the map.
   ═══════════════════════════════════════════════════════════════════════ */
/* workbench = Command OS desk (Soft·127). Missing entry → #/ops/workbench 404s. */
const OPS_TABS = ["map","overview","ai","workbench","zones","areas","dispatch","devices","alarms","resources"];

const SUBS = {
  activate: ["identify","verify","claim","interfaces","capabilities","position","done"],
  /* Match Response TABS exactly — Soft ghost desk aliases deleted Page39. */
  /* health/diagnose/groups/external → route-mask aliases (not mounted Fleet seats). */
  fleet:    ["firmware","zones","device","group"],
  response: ["board","assign","roster","cell","cases","robots"],
  dispatch: ["overview","people","robots","drones"],
  places: ["sites","zones","groups"],
  prepare:  ["readiness","drill","playbook"],
  skills: ["library","assign","waves"],
  cameras: ["live","retained","manage"],
  automations: ["library","runs","exceptions","safety","legacy-rules"],
  ota: ["base","home"],
  edge:     ["estate","link","share","skills","tunnel","bench","vision","locate","models","runtime","board","mesh","store","security","spatial"],
  /* Match SETTINGS_NAV — extra Soft desk URLs must 404 until nav + pane ship together. */
  settings: ["profile","security","sessions","delegations","users","notify","alarm","trigger","ai",
             "integrate","audit","billing","ota","danger"],
  control:  ["domains","custody","ng-shares","federation","shares","security","backup","reproduction"],
  sa:       ["caps","models","ai-tools","edge-lab","team","tenant","custody","factory","audit","vault"],
};

/* Sub-routes that name a single record. Without the record they address
   nothing, so they are a 404 rather than a page about a missing id. */
const NEEDS_ARG = new Set(["fleet/device","fleet/group"]);

const page = (key, Comp, def) => ({ seg, toast }) => {
  const sub = seg[2] || def;
  if (key === "activate" && activatePreviewV3()) {
    return html`<${Comp} toast=${toast} nav=${go}/>`;
  }
  if(!SUBS[key].includes(sub)) return html`<${System} which="404" toast=${toast}/>`;
  if(NEEDS_ARG.has(key+"/"+sub) && !seg[3]) return html`<${System} which="404" toast=${toast}/>`;
  return html`<${Comp} sub=${sub} arg=${seg[3]} toast=${toast} nav=${go}/>`;
};

const ROUTES = {
  activate: page("activate", Activate,     "identify"),
  response: page("response", Response,     "board"),
  dispatch: page("dispatch", DispatchPrimary, "people"),
  places: page("places", PlacesPrimary, "zones"),
  prepare:  page("prepare", PreparePrimary, "readiness"),
  skills:   page("skills", SkillsPrimary, "library"),
  cameras:  page("cameras", CamerasPrimary, "live"),
  automations: page("automations", AutomationsPrimary, "library"),
  ota:      page("ota", OtaPrimary, "home"),
  fleet:    page("fleet",    Fleet,        "firmware"),
  edge:     page("edge",     EdgeOS,       "estate"),
  settings: page("settings", Settings,     "profile"),
  control:  page("control",  PlatformControl, "shares"),
  sa:       page("sa",       SA,           "caps"),
};

/* ═══════════════════ app ═══════════════════════════════════════════════ */
function App(){
  const { me, ready, offline } = useSession();
  const [rawRoute] = useHash("/ops");
  const canon = resolveCanon(rawRoute);
  const route = parseHashRoute(canon).pathname;
  const [open,setOpen] = useState(null);
  const [pal,setPal] = useState(false);
  const [toasts,toast] = useToasts();
  const clock = useClock(me);
  const [cold,setCold] = useState(()=>!sessionStorage.getItem("warm"));
  useOverlayLock(cold && !!me);
  /* FOUND-2: stamp host-local so Class C Soft theatre CSS fail-closed on prod. */
  useEffect(() => {
    try {
      const local = isLocalHost();
      document.documentElement.dataset.cmcLocal = local ? "1" : "0";
      document.documentElement.dataset.cmcFound = "found-2";
      document.documentElement.dataset.cmcQuality = "quality-1";
      assertNoServiceWorkerRegistered();
    } catch (_) { /* ignore */ }
  }, []);
  const bare = route.startsWith("/auth") || route.startsWith("/system");
  const seg = route.split("/");
  const isMap = route === "/ops" || (route.startsWith("/ops/") && OPS_TABS.includes(seg[2]));
  const isResponse = route === "/response" || route.startsWith("/response/");
  const isPrepare = route === "/prepare" || route.startsWith("/prepare/");
  const isDispatch = route === "/dispatch" || route.startsWith("/dispatch/");
  const isPlaces = route === "/places" || route.startsWith("/places/");
  const isSkills = route === "/skills" || route.startsWith("/skills/");
  const isCameras = route === "/cameras" || route.startsWith("/cameras/");
  const isCamCinema = route === "/cameras" || route === "/cameras/live"
    || (route.startsWith("/cameras/") && (seg[2] === "live" || !seg[2]));
  const isMachine = route === "/machine" || route.startsWith("/machine/");
  const isSettings = route === "/settings" || route.startsWith("/settings/");
  const isActivateV3 = (route === "/activate" || route.startsWith("/activate/")) && activatePreviewV3();
  const isAutomations = route === "/automations" || route.startsWith("/automations/");
  const isOta = route === "/ota" || route.startsWith("/ota/");
  const isSa = route === "/sa" || route.startsWith("/sa/");
  const isFleet = route === "/fleet" || route.startsWith("/fleet/");
  const isEdge = route === "/edge" || route.startsWith("/edge/");
  const isControl = route === "/control" || route.startsWith("/control/");
  /* These primaries own their wayfinding; a second location strip obscures their headers. */
  const showLocBar = !bare && !!me && !isMap && !isResponse && !isPrepare && !isDispatch && !isPlaces
    && !isSkills && !isCameras && !isMachine && !isSettings && !isActivateV3
    && !isAutomations && !isOta && !isSa && !isFleet && !isEdge && !isControl;
  const routePrimary = primaryOf(route);
  /* ROUTE-2 paint veil + AnimatePresence stage wait SEALED.
     Instant primary swap — no exit fence, no Opening overlay. */
  const paintLag = false;

  useEffect(()=>{
    const on = e => {
      if((e.metaKey||e.ctrlKey) && e.key.toLowerCase()==="k"){ e.preventDefault(); setPal(p=>!p); }
    };
    addEventListener("keydown",on); return ()=>removeEventListener("keydown",on);
  },[]);
  useEsc(useCallback(()=>{ setPal(false); setOpen(null); },[]));
  useEffect(()=>{
    if(!open) return;
    const on = ()=>setOpen(null);
    const id = setTimeout(()=>addEventListener("click",on),0);
    return ()=>{ clearTimeout(id); removeEventListener("click",on); };
  },[open]);
  useEffect(()=>{ setOpen(null); },[route]);

  const panelTab = !seg[2] || seg[2]==="map" ? "overview" : (seg[2]==="areas" ? "zones" : seg[2]);
  const known = k => ROUTES[k];

  /* A private route without a session goes to sign-in, and a sign-in screen
     with a session goes to the map. Both wait for `ready` — routing on a
     half-answered probe is how an operator gets bounced to the login page
     mid-shift because one request was slow.

     `offline` is the third case: the cookie is still there but the server is
     unreachable. That is a connectivity problem, not a sign-out, so hold the
     route and let the session provider keep retrying.                        */
  useEffect(()=>{
    if(!ready || offline) return;
    if(!me && !bare) go("/auth/login");
    else if(me && route.startsWith("/auth") && !route.startsWith("/auth/signout")) go("/ops");
  },[ready, me, bare, route, offline]);

  /* Rewrite overlapping bookmarks to the singular canonical hash (once). */
  useEffect(()=>{
    const hit = resolveCanon(rawRoute);
    if (hit && hit !== rawRoute) go(hit);
  }, [rawRoute]);

  let body;
  /* Until the probe answers, a private route shows the readiness screen. The
     alternative is painting a console around a user who may not be signed in,
     then yanking it away.                                                   */
  if(!ready && !bare)                       body = html`<${System} which="boot" toast=${toast}/>`;
  /* Session intact, server unreachable, nothing cached to paint yet. 502 is
     already the screen that says exactly this, and the provider is retrying
     behind it — so no new page for a state we already describe.              */
  else if(offline && !me && !bare)          body = html`<${System} which="502" toast=${toast}/>`;
  else if(route.startsWith("/auth/signup")) body = html`<${Signup} toast=${toast}/>`;
  else if(route.startsWith("/auth/forgot")) body = html`<${Forgot} toast=${toast}/>`;
  else if(route.startsWith("/auth/signout"))body = html`<${SignOut} toast=${toast}/>`;
  else if(route.startsWith("/auth"))        body = html`<${Login} toast=${toast}/>`;
  else if(route.startsWith("/system"))      body = html`<${System} which=${seg[2]||"boot"} toast=${toast}/>`;
  /* Hash is typeable. Missing seat → 404, never a locked-door 403 that advertises the surface. */
  else if(seg[1]==="sa" && !navFor(me).some(n=>n.id==="sa"))
                                            body = html`<${System} which="404" toast=${toast}/>`;
  else if((seg[1]==="control" || seg[1]==="fleet" || seg[1]==="edge") && !canOpenControl(me))
                                            body = html`<${System} which="404" toast=${toast}/>`;
  else if(seg[1]==="ota" && !navFor(me).some(n=>n.id==="ota"))
                                            body = html`<${System} which="404" toast=${toast}/>`;
  else if(seg[1]==="activate" && !canOpenActivateSeat(me))
                                            body = html`<${System} which="404" toast=${toast}/>`;
  else if(seg[1]==="skills" && !navFor(me).some(n=>n.id==="skills"))
                                            body = html`<${System} which="404" toast=${toast}/>`;
  else if(isMap)                            body = html`<${OpsMap} panelTab=${panelTab} toast=${toast}/>`;
  else if(seg[1]==="machine" && seg[2]==="groups")
                                            body = html`<${MachineGroups} groupId=${seg[3] ? decodeURIComponent(seg[3]) : ""} toast=${toast}/>`;
  else if(seg[1]==="machine" && seg[2]==="capabilities")
                                            body = html`<${MachineCapDeploy} toast=${toast}/>`;
  else if(seg[1]==="machine" && seg[2]==="deployments")
                                            body = html`<${MachineCapDeploy} toast=${toast}/>`;
  else if(seg[1]==="machine" && seg[2])
                                            body = html`<${MachineDetail} id=${decodeURIComponent(seg[2])} facet=${seg[3] || "overview"} toast=${toast}/>`;
  else if(seg[1]==="machine")               body = html`<${MachineDeck} toast=${toast}/>`;
  else if(known(seg[1]))                    body = known(seg[1])({ seg, toast });
  else                                      body = html`<${System} which="404" toast=${toast}/>`;

  /* Auth/system/map/Response/Cam cinema: plain stage (no wipe · full immersion).
     Other primaries: enter-only fluid motion — NO AnimatePresence wait (ROUTE-2 sealed). */
  const stageKey = bare ? route : routePrimary;
  const stage = bare || isMap || isResponse || isCamCinema
    ? html`<div className="page__stage" id="cmc-main" tabIndex=${-1}
        data-map=${isMap ? "1" : undefined}
        data-cmc-response=${isResponse ? "1" : undefined}
        data-cmc-cinema=${isCamCinema ? "1" : undefined}
        data-cmc-scroll=${!bare && !isMap && !isResponse && !isCamCinema ? "workplane" : undefined}>${body}</div>`
    : html`<${M.div} className="page__stage page__stage--fluid" key=${stageKey} id="cmc-main" tabIndex=${-1}
        data-cmc-route-fence="live" data-cmc-motion="stage-enter"
        data-cmc-scroll="workplane"
        initial=${{ opacity: 0, y: 12 }}
        animate=${{ opacity: 1, y: 0 }}
        transition=${STAGE}>
        ${body}
      <//>`;

  const skipToPage = () => {
    const el = document.getElementById("cmc-main");
    if (!el) return;
    try { el.focus({ preventScroll: false }); } catch (_) { try { el.focus(); } catch (__) { /* ignore */ } }
  };

  const shell = html`
    <div className="page" data-cmc-paint-lag=${paintLag ? "1" : "0"} data-cmc-shell="shell-1"
      data-cmc-locbar=${showLocBar ? "1" : undefined}
      data-cmc-cinema=${isCamCinema ? "1" : undefined}>
      ${!bare ? html`<button type="button" className="cmc-skip" onClick=${skipToPage}>Skip to page</button>` : null}
      <div className="aura"><i/><i/><i/></div>
      ${!bare && me && !isCamCinema && html`<${TopBar} route=${route} clock=${clock} open=${open}
        setOpen=${setOpen} onPalette=${()=>setPal(true)} me=${me}/>`}
      ${showLocBar && html`<${LocationBar} route=${route} siteLabel=${me.tenant || me.username}/>`}
      ${!bare && me && !isCamCinema && html`<${QualityNetBanner} primary=${routePrimary}/>`}
      ${stage}
      <${RoutePaintVeil} targetPrimary=${routePrimary} show=${false}/>
      <${AnimatePresence}>${pal && html`<${Palette} open=${pal} onClose=${()=>setPal(false)} toast=${toast}/>`}<//>
      <${Toasts} list=${toasts}/>
      <${Overlay} show=${cold && !!me} title="Bringing the console up"
        say="Nothing is drawn from cache. Each line below is a precondition the console will not start without."
        steps=${COLD} onDone=${()=>{ sessionStorage.setItem("warm","1"); setCold(false); }}/>
    </div>`;

  /* Always keep LiveProvider mounted. Tearing it down when `me` clears remounts
     the Sign out confirmation and resets it to "ask" after the cookie is gone. */
  return html`<${LiveProvider} armed=${!!me}>${shell}<//>`;
}

/* A screen that throws must land on the 500 page, not on a blank document.
   This is the only place in the console that swallows an exception.        */
class Fuse extends React.Component {
  constructor(p){ super(p); this.state = { blown:null }; }
  static getDerivedStateFromError(e){ return { blown:e }; }
  componentDidCatch(e){
    try { window.__fuse = String((e && e.stack) || (e && e.message) || e); } catch (_e) {}
    console.error("[console] render failed", e);
  }
  render(){
    if(!this.state.blown) return this.props.children;
    return html`<${System} which="500" toast=${()=>{}}/>`;
  }
}

createRoot(document.getElementById("root")).render(
  html`<${Fuse}><${SessionProvider}><${App}/><//><//>`);

/* The splash is dismissed only once React has painted something. */
requestAnimationFrame(()=>requestAnimationFrame(()=>{
  const s = document.getElementById("splash");
  if(!s) return;
  s.dataset.off = "1";
  setTimeout(()=>s.remove(), 600);
}));
